// Reach block (node page): the stored per-node figures from GET /api/audit/reach and the
// small pure helpers the block draws with. No imports, so the frontend tests can compile it.

export interface ReachListener {
  key: string;
  name: string;
  lat: number | null;
  lon: number | null;
  min_hops: number;
  appearances: number;
  /** Newest hourly row of this observer for the node (ISO); absent in snapshots stored before it was added. */
  last_seen?: string | null;
}

export interface ReachFurthest {
  key: string;
  name: string;
  distance_km: number;
  min_hops: number;
  /** That observer's appearances; absent in older stored snapshots. */
  appearances?: number;
}

export interface ReachBlock {
  node: string;
  window: string;
  generated_at: string;
  name: string | null;
  listener_count: number;
  /** Distinct observers active in the window; absent in older stored snapshots. */
  observer_total?: number;
  nearest_hops: number | null;
  typical_hops: number | null;
  observations: number;
  furthest: ReachFurthest | null;
  listeners: ReachListener[];
  /** Observers active in the window (the ones observer_total counts); absent in older stored snapshots. */
  active_observers?: ReachObserver[];
  /** Hourly arrays over the window; absent in snapshots stored before Trends, empty arrays for a node nobody heard. */
  trends?: ReachTrends;
}

/** The Trends block data: parallel arrays, one entry per hour, oldest first; null = no data that hour (never zero). */
export interface ReachTrends {
  hour_start: string[];
  packets: (number | null)[];
  observers: (number | null)[];
  snr_db: (number | null)[];
}

export interface ReachObserver {
  key: string;
  name: string;
}

export const REACH_WINDOWS = ["24h", "7d"] as const;
export type ReachWindow = (typeof REACH_WINDOWS)[number];
export const REACH_DEFAULT_WINDOW: ReachWindow = "7d";
export const REACH_TITLE = "Reach";
export const REACH_EMPTY_TEXT = "No Observers In This Window";

// Ring 0 is Direct (the observer heard the node's own packet with no relay), then rings 1 to 4,
// then one ring for everything 5 hops and further. The Direct ring is drawn only when some
// observer is in it.
export const REACH_RING_COUNT = 5;

export function reachRing(minHops: number): number {
  return Math.min(Math.max(Math.round(minHops), 0), REACH_RING_COUNT);
}

export function reachRingLabel(ring: number): string {
  if (ring <= 0) return "Direct";
  return ring >= REACH_RING_COUNT ? `${REACH_RING_COUNT}+` : String(ring);
}

// The colour tokens live in global.css next to the rf tokens.
export function reachHopColorVar(minHops: number): string {
  return `--reach-hop-${reachRing(minHops)}`;
}

// The legend under the ring picture: This repeater, Direct (only when present), 1, 2, 3, 4, 5+.
// "This repeater" (the centre dot) comes first, with ring -1 standing for it.
export const REACH_THIS_REPEATER = "This repeater";
export function reachLegend(listeners: ReachListener[]): { ring: number; label: string }[] {
  const rings = listeners.some((l) => reachRing(l.min_hops) === 0) ? [0, 1, 2, 3, 4, 5] : [1, 2, 3, 4, 5];
  return [{ ring: -1, label: REACH_THIS_REPEATER }, ...rings.map((ring) => ({ ring, label: reachRingLabel(ring) }))];
}

// "Hops" wording for one observer's distance: "Direct", "1 hop", "3 hops".
export function reachHopsText(minHops: number): string {
  return minHops <= 0 ? "Direct" : `${n0(minHops)} ${plural(minHops, "hop", "hops")}`;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

export const REACH_CAPTION = [
  { label: "Hop count", value: "from the observer" },
  { label: "Direct", value: "heard this repeater's own packet with no relay" },
  { label: "1 hop", value: "heard this repeater relaying" },
  { label: "Ring position", value: "no direction" },
] as const;

const n0 = (v: number) => v.toLocaleString("en-US");

// "Heard by 51 of 53 observers, 4 of them direct · Nearest: Direct · Furthest: Catalyst-Central, 489.6 mi (788 km), 8 hops"
// Without the observer total (an older stored snapshot) it reads "Heard by 48 observers".
export function reachFiguresLine(b: ReachBlock, formatKm: (km: number) => string): string {
  const total = b.observer_total && b.observer_total >= b.listener_count ? b.observer_total : null;
  let heard = total === null
    ? `Heard by ${n0(b.listener_count)} ${plural(b.listener_count, "observer", "observers")}`
    : `Heard by ${n0(b.listener_count)} of ${n0(total)} ${plural(total, "observer", "observers")}`;
  const direct = reachDirectCount(b.listeners);
  if (direct > 0) heard += `, ${n0(direct)} of them direct`;
  const nearest = b.nearest_hops ?? 0;
  const parts = [heard, `Nearest: ${nearest <= 0 ? "Direct" : `${nearest} ${plural(nearest, "hop", "hops")}`}`];
  if (b.furthest) {
    const f = b.furthest;
    const hops = f.min_hops <= 0 ? "heard direct" : `${n0(f.min_hops)} ${plural(f.min_hops, "hop", "hops")}`;
    parts.push(`Furthest: ${f.name}, ${formatKm(f.distance_km)}, ${hops}`);
  }
  return parts.join(" \u00b7 ");
}

// The label on the map line to the furthest observer: it is the fewest relays on the route, not an RF distance.
export function reachFurthestLineLabel(minHops: number): string {
  return minHops <= 0 ? "heard direct" : `via ${n0(minHops)} ${plural(minHops, "hop", "hops")}`;
}

// The observer table's last column and packets column, named for what they hold.
export const REACH_LAST_HEARD_HEADER = "Last Heard (Hour)";
export function reachPacketsHeader(win: string): string {
  return `Packets (${win})`;
}

// Observers per ring, rings 1 to 4 and then 5 and further (Direct is counted by reachDirectCount).
export function reachRingCounts(listeners: ReachListener[]): number[] {
  const counts = Array.from({ length: REACH_RING_COUNT }, () => 0);
  for (const l of listeners) if (reachRing(l.min_hops) >= 1) counts[reachRing(l.min_hops) - 1] += 1;
  return counts;
}

export function reachDirectCount(listeners: ReachListener[]): number {
  return listeners.filter((l) => reachRing(l.min_hops) === 0).length;
}

// "Direct 3 · 1 Hop 8 · 2 Hops 8 · 3 Hops 8 · 4 Hops 7 · 5+ Hops 17" (Direct only when some observer heard it directly)
export function reachRingCountsLine(listeners: ReachListener[]): string {
  const direct = reachDirectCount(listeners);
  const hops = reachRingCounts(listeners).map((n, i) => `${reachRingLabel(i + 1)} ${i === 0 ? "Hop" : "Hops"} ${n0(n)}`);
  return (direct > 0 ? [`Direct ${n0(direct)}`, ...hops] : hops).join(" \u00b7 ");
}

// The observers active in the window that have no row for this node, by name.
export function reachNotHeardBy(block: Pick<ReachBlock, "listeners" | "active_observers">): ReachObserver[] {
  const heard = new Set(block.listeners.map((l) => l.key));
  return (block.active_observers ?? []).filter((o) => !heard.has(o.key));
}

// "Not heard by: X, Y"; null when there is nothing to list (or an older snapshot without the list).
export function reachNotHeardByLine(missing: ReachObserver[]): string | null {
  return missing.length === 0 ? null : `Not heard by: ${missing.map((o) => o.name).join(", ")}`;
}

// The data is hourly: the hour row an observer was last seen in, in local time with the zone label
// ("Today 13:00 MDT" for today, "Oct 6 13:00 MDT" for an earlier day). `tz` is the viewer's zone abbreviation
// (tzAbbrev in rfHealth, the helper the Signal axis uses); this file has no imports.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function reachLastHour(iso: string | null | undefined, tz: string, now: Date = new Date()): string {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "\u2014";
  const hh = `${String(d.getHours()).padStart(2, "0")}:00${tz ? ` ${tz}` : ""}`;
  const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return sameDay ? `Today ${hh}` : `${MONTHS[d.getMonth()]} ${d.getDate()} ${hh}`;
}

export const REACH_LIST_SHOWN = 10;

// Observer list order: fewest hops first, then most packets, then key for a stable order.
export function reachListOrder(listeners: ReachListener[]): ReachListener[] {
  return [...listeners].sort((a, b) => a.min_hops - b.min_hops || b.appearances - a.appearances || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
}

export function reachListToggleLabel(total: number, showAll: boolean): string {
  return showAll ? "Show Fewer" : `Show All ${n0(total)} Observers`;
}

// "<name> · <n> hops · <appearances>"
export function reachDotTip(l: ReachListener): string {
  return `${l.name} · ${l.min_hops <= 0 ? "direct" : `${l.min_hops} ${plural(l.min_hops, "hop", "hops")}`} · ${l.appearances}`;
}

export interface RingDot {
  listener: ReachListener;
  ring: number;
  x: number;
  y: number;
}

// Each listener sits on its ring, spread evenly around it; every ring starts at a
// different angle so the dots of neighbouring rings do not line up.
// With a Direct ring the picture has one more ring, innermost.
export function reachRingOffset(listeners: ReachListener[]): number {
  return reachDirectCount(listeners) > 0 ? 1 : 0;
}

export function reachRingStep(listeners: ReachListener[], size: number): number {
  return (size / 2 - 12) / (REACH_RING_COUNT + reachRingOffset(listeners));
}

export function reachRingLayout(listeners: ReachListener[], size: number): RingDot[] {
  const c = size / 2;
  const offset = reachRingOffset(listeners);
  const step = reachRingStep(listeners, size);
  const byRing = new Map<number, ReachListener[]>();
  for (const l of listeners) {
    const r = reachRing(l.min_hops);
    const arr = byRing.get(r);
    if (arr) arr.push(l);
    else byRing.set(r, [l]);
  }
  const out: RingDot[] = [];
  for (const [ring, list] of byRing) {
    const radius = step * (ring + offset);
    list.forEach((listener, i) => {
      const a = -Math.PI / 2 + ring * 0.7 + (2 * Math.PI * i) / list.length;
      out.push({ listener, ring, x: c + radius * Math.cos(a), y: c + radius * Math.sin(a) });
    });
  }
  return out;
}
